import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Switch,
  Space,
  Popconfirm,
  Row,
  Col,
  Typography,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ProjectOutlined,
  GithubOutlined,
  LinkOutlined,
  CodeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { MentorProjectResponse, CreateMentorProjectRequest, UpdateMentorProjectRequest } from '../../../types';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ProjectsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<MentorProjectResponse | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const { data: projects = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.projects,
    queryFn: async () => {
      const response = await apiService.getMentorProjects();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateMentorProjectRequest) => {
      const response = await apiService.createMentorProject(data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Proje başarıyla eklendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.projects });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMentorProjectRequest }) => {
      const response = await apiService.updateMentorProject(id, data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Proje başarıyla güncellendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.projects });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiService.deleteMentorProject(id);
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      toast.success('Proje başarıyla silindi.');
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.projects });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const handleAdd = () => {
    setEditingProject(null);
    form.resetFields();
    form.setFieldsValue({
      isCompleted: false
    });
    setModalVisible(true);
  };

  const handleEdit = (project: MentorProjectResponse) => {
    setEditingProject(project);
    form.setFieldsValue({
      projectName: project.projectName,
      projectDescription: project.projectDescription,
      githubUrl: project.githubUrl,
      liveUrl: project.liveUrl,
      technologies: project.technologies,
      startDate: project.startDate ? dayjs(project.startDate) : null,
      endDate: project.endDate ? dayjs(project.endDate) : null,
      isCompleted: project.isCompleted
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const projectData: CreateMentorProjectRequest & UpdateMentorProjectRequest = {
        projectName: values.projectName,
        projectDescription: values.projectDescription,
        githubUrl: values.githubUrl,
        liveUrl: values.liveUrl,
        technologies: values.technologies,
        startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : undefined,
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
        isCompleted: values.isCompleted
      };
      if (editingProject) {
        updateMutation.mutate({ id: editingProject.id, data: projectData });
      } else {
        createMutation.mutate(projectData);
      }
    } catch {
      // Form validation failed
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingProject(null);
    form.resetFields();
  };

  const filteredProjects = projects.filter(project =>
    project.projectName.toLowerCase().includes(searchText.toLowerCase()) ||
    (project.technologies && project.technologies.toLowerCase().includes(searchText.toLowerCase())) ||
    (project.projectDescription && project.projectDescription.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Proje Adı',
      dataIndex: 'projectName',
      key: 'projectName',
      sorter: (a: MentorProjectResponse, b: MentorProjectResponse) =>
        a.projectName.localeCompare(b.projectName),
    },
    {
      title: 'Teknolojiler',
      dataIndex: 'technologies',
      key: 'technologies',
      render: (technologies: string) => (
        <div>
          {technologies ? technologies.split(',').map((tech, index) => (
            <Tag key={index} color="blue" style={{ margin: '2px' }}>
              {tech.trim()}
            </Tag>
          )) : '-'}
        </div>
      ),
    },
    {
      title: 'Başlangıç Tarihi',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
      sorter: (a: MentorProjectResponse, b: MentorProjectResponse) =>
        new Date(a.startDate || '').getTime() - new Date(b.startDate || '').getTime(),
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isCompleted',
      key: 'isCompleted',
      filters: [
        { text: 'Tamamlandı', value: true },
        { text: 'Devam Ediyor', value: false },
      ],
      onFilter: (value: boolean | React.Key, record: MentorProjectResponse) => record.isCompleted === value,
      render: (isCompleted: boolean) => (
        <Tag color={isCompleted ? 'green' : 'orange'}>
          {isCompleted ? 'Tamamlandı' : 'Devam Ediyor'}
        </Tag>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorProjectResponse) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            style={{
              backgroundColor: '#8b949e',
              borderColor: '#8b949e',
              color: 'white',
              fontWeight: 500
            }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu projeyi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
            okButtonProps={{
              style: {
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }
            }}
            cancelButtonProps={{
              style: {
                backgroundColor: 'white',
                borderColor: '#d1d5db',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }
            }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard
      title="Projeler"
      description="Proje bilgilerinizi ekleyin veya düzenleyin."
    >
      <style>
        {`
          .ant-btn:not(.ant-btn-primary):hover {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):focus {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):active {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <TabListLoading loading={loading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Ara..."
              allowClear
              style={{ width: 300 }}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: '#8b949e',
                borderColor: '#8b949e',
                color: 'white',
                fontWeight: 500
              }}
            >
              Proje Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={filteredProjects}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSizeOptions: ['5', '10', '20', '50'],
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} / ${total} proje`,
        }}
      />
      </TabListLoading>

      <Modal
        title={editingProject ? 'Proje Düzenle' : 'Yeni Proje'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingProject ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: {
            backgroundColor: 'var(--text-secondary)',
            borderColor: 'var(--text-secondary)',
            color: 'white',
            fontWeight: 500,
            height: '32px',
            minWidth: '80px',
            filter: 'none'
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500,
            height: '32px',
            minWidth: '80px',
            filter: 'none'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isCompleted: false
          }}
        >
          <Form.Item
            name="projectName"
            label="Proje Adı"
            rules={[{ required: true, message: 'Lütfen proje adını girin!' }]}
          >
            <Input prefix={<ProjectOutlined />} placeholder="Proje Adı" />
          </Form.Item>
          
          <Form.Item
            name="projectDescription"
            label="Proje Açıklaması"
          >
            <TextArea rows={4} placeholder="Projenin detaylı açıklaması" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="githubUrl"
                label="GitHub URL"
              >
                <Input prefix={<GithubOutlined />} placeholder="https://github.com/..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="liveUrl"
                label="Canlı URL"
              >
                <Input prefix={<LinkOutlined />} placeholder="https://example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="technologies"
            label="Teknolojiler"
          >
            <Input prefix={<CodeOutlined />} placeholder="React, TypeScript, Node.js (virgülle ayırın)" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Başlangıç Tarihi"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Başlangıç tarihi bugünden sonra olamaz'));
                      }
                      const endDate = form.getFieldValue('endDate');
                      if (endDate && selectedDate.isAfter(dayjs(endDate).startOf('day'))) {
                        return Promise.reject(new Error('Başlangıç tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="YYYY-MM-DD"
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="Bitiş Tarihi"
                dependencies={['startDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const startDate = form.getFieldValue('startDate');
                      if (startDate && dayjs(value).isBefore(dayjs(startDate))) {
                        return Promise.reject(new Error('Bitiş tarihi başlangıç tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="YYYY-MM-DD"
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && current) {
                      return current.isBefore(dayjs(startDate), 'day');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isCompleted"
            label="Tamamlandı mı?"
            valuePropName="checked"
            dependencies={['startDate']}
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const startDate = form.getFieldValue('startDate');
                  if (startDate && dayjs(startDate).isAfter(dayjs(), 'day')) {
                    return Promise.reject(new Error('Gelecekteki bir tarih için tamamlandı durumu seçilemez'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PanelTabCard>
    </>
  );
};

export default ProjectsTab;
